import { ref, watchEffect } from 'vue';
import type { Reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import usePrice from '@core/composables/use-price';
import { getImgCdnServiceInstance } from '@bc/shared/util';

import { CardProductProps } from '../types/card-product-props';
import { ProductBuilder } from '../services/product-builder';
import { isTileSimplified } from '../services/tile-simplification';
import { AksSystem } from '@bc/discovery/domain/catalog';
import { filterIconConfig } from '@client/modules/discovery/feature/catalog/components/filter-sidebar/filter-icon-config';

export const useProductViewModelFull = (props: Reactive<CardProductProps>) => {
  const { getConvertedPrice } = usePrice();
  const i18n = useI18n();
  const productViewModel = ref<Awaited<ReturnType<ProductBuilder['build']>>>();

  watchEffect(async () => {
    productViewModel.value = await new ProductBuilder(getImgCdnServiceInstance(), i18n, getConvertedPrice)
      .with('id', props.product.id)
      .with('name', props.product.name as string)
      .with('images', props.product.images)
      .with('link', props.product.link)
      .with('variants', props.product.variants)
      .with('brand', props.product.brand)
      .with('brandName', props.product.brand.name)
      .with('brandLink', props.product.brand.link)
      .with('discountPercentage', props.product.discount_rate)
      .with('simplified', isTileSimplified())
      .with('unitMultiplier', props.product.unit_multiplier)
      .with('tags', props.product.tags as string[])
      .with('isNew', props.product.is_new)
      // Remove `canDisplayProvenSuccessBadge` after experiment RET-3604 is finished
      .with(
        'isProvenSuccess',
        props.canDisplayProvenSuccessBadge && props.product.aks_systems?.includes(AksSystem.PROVEN_SUCCESS)
      )
      .with(
        'systemIcons',
        (props.product.aks_systems ?? []).flatMap((s) => {
          const config = filterIconConfig[s];
          if (!config) {return [];}
          return [config];
        })
      )
      .with('ad', props.product.ad)
      .with('wholeSalePrice', props.product.wholesale_price)
      .with('originalWholeSalePrice', props.product.original_wholesale_price)
      .with('retailPrice', props.product.retail_price)
      .with('uuid', props.product.uuid)
      .with('available_at', props.product.available_at)
      .with('estimatedDelivery', props.product.estimated_delivery)
      .build();
  });

  return {
    productViewModel,
  };
};
